import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useProgrammeStore, useLanguageStore, useNavigationStore } from '@/stores'
import { factory } from '../../../../factory-api/factory'
import type { NotificationMessage } from '@/generated-api'
import { usePushNotificationStore } from './pushNotificationStore'
import router from '@/router'

export const useNotificationCenterStore = defineStore('NotificationCenterStore', () => {
  // State
  const initialized = ref(false)
  const centerNotifications = ref<NotificationMessage[]>([])
  const programmeStore = useProgrammeStore()
  const languageStore = useLanguageStore()
  const pushNotificationStore = usePushNotificationStore()
  const pollingInterval = ref<number | null>(null)
  const POLL_INTERVAL = 1000 * 60 * 5 // 5 minute
  const navigationStore = useNavigationStore()

  // Actions
  const init = async () => {
    await getCenterNotifications()
    await pushNotificationStore.resaveTokenAfterLogin()
    startPolling()
    initialized.value = true
  }

  const markAsReadBatch = async (notificationIds: string[]) => {
    if (!notificationIds.length) {
      return
    }

    // Update client side state first for immediate UI feedback
    centerNotifications.value.forEach((notification) => {
      if (notification.id) {
        notification.isRead = true
      }
    })

    // Then make API call to persist changes
    try {
      const notificationCenterApi = await factory.getNotificationCenterApi()
      await notificationCenterApi.markNotificationsAsRead({
        requestBody: notificationIds
      })
    } catch (error) {
      console.error('Failed to mark notifications as read', error)
    }
  }

  const markAllAsRead = async () => {
    const unreadNotificationIds: string[] = []

    centerNotifications.value.forEach((notification) => {
      if (notification.isRead === false && notification.id) {
        unreadNotificationIds.push(notification.id)
      }
    })

    await markAsReadBatch(unreadNotificationIds)
  }

  const newCount = computed(
    () =>
      centerNotifications.value.filter(
        (notification: NotificationMessage) => notification.isRead === false
      ).length
  )

  const getCenterNotifications = async () => {
    const notificationCenterApi = await factory.getNotificationCenterApi()
    const programmeId = programmeStore.selectedProgramme?.programmeId || ''

    const response = await notificationCenterApi.getNotifications({
      programmeId: programmeId,
      language: languageStore.currentLocale
    })

    if (response !== null) {
      centerNotifications.value = response
    }
  }

  // Start polling for new notifications
  const startPolling = () => {
    // Clear any existing interval to prevent duplicates
    stopPolling()

    // Set new interval
    pollingInterval.value = window.setInterval(async () => {
      await getCenterNotifications()
      console.log('Checking for new notifications...')
    }, POLL_INTERVAL)
  }

  // Stop polling
  const stopPolling = () => {
    if (pollingInterval.value !== null) {
      window.clearInterval(pollingInterval.value)
      pollingInterval.value = null
    }
  }

  const goToLink = async (notification: NotificationMessage) => {
    try {
      await router.isReady()

      if (notification.regardingEntity === 'eg_programme_activity') {
        await router.push({
          path: navigationStore.navigationOptions('calendarsection').navItem?.url + '?calenderId=',
          query: { calenderId: notification.regardingId?.toLowerCase() }
        })
      } else if (notification.regardingEntity === 'eg_evaluation') {
        await router.push({
          path: navigationStore.navigationOptions('evaluationsection').navItem?.url
        })
      }
    } catch (error) {
      console.error('Error navigating to link:', error)
    }
  }

  return {
    initialized,
    init,
    centerNotifications,
    newCount,
    markAllAsRead,
    goToLink,
    startPolling,
    stopPolling
  }
})
